import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { openReceipt } from "../lib/receipts";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import { Plane, Plus, X, AlertTriangle, CheckCircle2, MapPin, Calendar, DollarSign, FileText, ShieldCheck, Trash2, Building2, Receipt as ReceiptIcon, ListChecks, Paperclip, Download, RefreshCw, RotateCcw } from "lucide-react";
import ReceiptUploader from "../components/ReceiptUploader";
import CurrencySelect, { currencySymbol } from "../components/CurrencySelect";
import { exportCsv } from "../lib/exportCsv";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Trigger a browser download for a Blob response (used by PDF exports).
const downloadBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; document.body.appendChild(a); a.click();
  document.body.removeChild(a); window.URL.revokeObjectURL(url);
};

// Fallback list used when /travel-expense-types hasn't loaded yet — these are the seed
// categories shipped with a default travel policy. Real source of truth is the active
// travel policy, exposed by GET /api/travel-expense-types.
const DEFAULT_CATEGORIES = [
  { key: "meals", label: "Meals" },
  { key: "lodging", label: "Lodging" },
  { key: "transport", label: "Transport" },
  { key: "mileage", label: "Mileage" },
  { key: "per_diem", label: "Per diem" },
  { key: "other", label: "Other" },
];

const statusBadge = (s, t) => {
  const map = {
    draft: "sw-badge-neutral",
    pending_approval: "sw-badge-pending",
    ready_for_finance: "sw-badge-gold",
    submitted_to_qbo: "sw-badge-gold",
    paid: "sw-badge-approved",
    rejected: "sw-badge-rejected",
  };
  return <span className={`sw-badge ${map[s] || "sw-badge-neutral"}`}>{t(`status.${s}`)}</span>;
};

function NewTravelModal({ policy, departments, expenseTypes, onClose, onSaved }) {
  const { t } = useI18n();
  const CATEGORIES = (expenseTypes && expenseTypes.length > 0) ? expenseTypes : DEFAULT_CATEGORIES;
  const firstKey = CATEGORIES[0]?.key || "other";
  const [form, setForm] = useState({ purpose: "", destination: "", departure_date: "", return_date: "", notes: "", currency: "USD" });
  const defaultDept = departments[0]?.id || "";
  const [items, setItems] = useState([{ category: firstKey, date: "", description: "", amount: "", miles: "", nights: "", department_id: defaultDept, receipt_url: "" }]);
  const [saving, setSaving] = useState(false);
  // FX rates: key "CURRENCY|YYYY-MM-DD" → USD-per-1-unit rate. USD short-circuits to 1.
  // We fetch on-demand as the user picks currencies so the live cap preview
  // matches what the backend will compute on submit (see /api/fx/rate).
  const [fxRates, setFxRates] = useState({});
  const fxKey = (cur, dt) => `${(cur || "USD").toUpperCase()}|${(dt || form.departure_date || "").slice(0, 10)}`;

  useEffect(() => {
    const cur = (form.currency || "USD").toUpperCase();
    if (cur === "USD") return;
    const need = new Set();
    items.forEach((it) => {
      const key = fxKey(cur, it.date);
      if (!(key in fxRates)) need.add(key);
    });
    if (!need.size) return;
    (async () => {
      const patch = {};
      for (const k of need) {
        const [c, dt] = k.split("|");
        const dtOk = /^\d{4}-\d{2}-\d{2}$/.test(dt) && dt >= "1999-01-01";
        try {
          const q = dtOk ? `?currency=${c}&on_date=${dt}` : `?currency=${c}`;
          const { data } = await api.get(`/fx/rate${q}`);
          patch[k] = data.rate;
        } catch {
          try {
            const { data } = await api.get(`/fx/rate?currency=${c}`);
            patch[k] = data.rate;
          } catch { patch[k] = null; }
        }
      }
      setFxRates((prev) => ({ ...prev, ...patch }));
    })();
  }, [items, form.departure_date, form.currency]);

  // USD-equivalent amount for policy checks. Uses the request-level currency
  // (Sprint 30 — single currency governs the entire request).
  const usdOf = (it) => {
    const raw = parseFloat(it.amount) || 0;
    const cur = (form.currency || "USD").toUpperCase();
    if (cur === "USD") return raw;
    const rate = fxRates[fxKey(cur, it.date)];
    if (rate == null) return raw;
    return raw * rate;
  };

  const total = items.reduce((s, i) => s + usdOf(i), 0);  // total sempre em USD

  const addItem = () => setItems([...items, { category: firstKey, date: "", description: "", amount: "", miles: "", nights: "", department_id: defaultDept, receipt_url: "" }]);
  const removeItem = (idx) => setItems(items.filter((_, i) => i !== idx));
  const updateItem = (idx, patch) => setItems(items.map((it, i) => (i === idx ? { ...it, ...patch } : it)));

  // Trip duration in days (inclusive) used for per_trip caps.
  const tripDays = (() => {
    if (!form.departure_date || !form.return_date) return 1;
    const d1 = new Date(form.departure_date);
    const d2 = new Date(form.return_date);
    const ms = d2 - d1;
    if (Number.isNaN(ms)) return 1;
    return Math.max(1, Math.round(ms / (1000 * 60 * 60 * 24)) + 1);
  })();

  // Sum amounts by category (across ALL items) and by (category, date) — in USD
  // so they can be compared apples-to-apples against the USD-denominated caps.
  const sumByCat = items.reduce((acc, it) => {
    acc[it.category] = (acc[it.category] || 0) + usdOf(it);
    return acc;
  }, {});
  const sumByCatDay = items.reduce((acc, it) => {
    const key = `${it.category}::${it.date || "—"}`;
    acc[key] = (acc[key] || 0) + usdOf(it);
    return acc;
  }, {});

  /** Returns a human-readable warning string for `it` or null if compliant.
   *  Mirrors the backend `_validate_travel_items_vs_policy` enforcement_mode logic so
   *  the UI flag matches exactly what the backend will compute on submit. */
  const checkPolicy = (it) => {
    const rule = policy?.categories?.find((c) => c.category === it.category);
    if (!rule) return null;
    const amt = usdOf(it);            // caps are USD → convert first
    const dl = parseFloat(rule.daily_limit) || 0;
    const mode = rule.enforcement_mode || "per_day";

    // Mileage is always reimbursed at $rate × miles (special case)
    if (rule.unit === "per_mile") {
      const miles = parseFloat(it.miles) || 0;
      const expected = miles * dl;
      if (miles > 0 && amt > expected * 1.05) {
        return `Exceeds policy ($${expected.toFixed(2)} for ${miles} mi @ $${dl}/mi)`;
      }
      return null;
    }

    if (dl <= 0) return null;

    if (mode === "per_item") {
      if (amt > dl) return `Exceeds policy ($${dl} per item)`;
      return null;
    }
    if (mode === "per_day") {
      const daySum = sumByCatDay[`${it.category}::${it.date || "—"}`] || 0;
      if (daySum > dl) {
        return `Exceeds policy: ${it.category} on ${it.date || "—"} sums $${daySum.toFixed(2)} > $${dl}/day`;
      }
      return null;
    }
    if (mode === "per_trip") {
      const cap = dl * tripDays;
      const total = sumByCat[it.category] || 0;
      if (total > cap) {
        return `Exceeds policy: ${it.category} trip total $${total.toFixed(2)} > cap $${cap.toFixed(2)} (${tripDays}d × $${dl})`;
      }
      return null;
    }
    return null;
  };

  // Aggregated warnings deduped per category so we render a single line per
  // category near the totals block (instead of repeating on every item row).
  // First offending item wins the message for that category — good enough
  // because per_day/per_trip messages already include the day/trip sum, and
  // per_item messages are identical for a given category.
  const categoryWarnings = (() => {
    const map = new Map();
    items.forEach((it) => {
      if (map.has(it.category)) return;
      const w = checkPolicy(it);
      if (w) map.set(it.category, w);
    });
    return Array.from(map.entries());
  })();

  // Whether a per-trip category is currently within cap (used to show a green status)
  const perTripStatus = (cat) => {
    const rule = policy?.categories?.find((c) => c.category === cat);
    if (!rule || (rule.enforcement_mode || "per_day") !== "per_trip") return null;
    const dl = parseFloat(rule.daily_limit) || 0;
    if (dl <= 0) return null;
    const cap = dl * tripDays;
    const total = sumByCat[cat] || 0;
    return { total, cap, ok: total <= cap, tripDays, dl };
  };

  const submit = async (e, asDraft = false) => {
    if (e) e.preventDefault();
    // Receipts are required for submission (not for drafts). Drafts let the user
    // attach receipts later before submitting.
    if (!asDraft) {
      const missing = items
        .map((it, idx) => ({ it, idx }))
        .filter(({ it }) => !it.receipt_url);
      if (missing.length > 0) {
        alert(
          `Anexo de recibo é obrigatório em todos os itens. Faltam ${missing.length} recibo(s) — itens: ${missing.map((m) => m.idx + 1).join(", ")}. Você pode "Salvar como rascunho" e completar depois.`
        );
        return;
      }
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        currency: (form.currency || "USD").toUpperCase(),
        items: items.map((it) => ({
          category: it.category,
          date: it.date,
          description: it.description || null,
          amount: parseFloat(it.amount) || 0,
          // Backend re-coerces currency at the request level (Sprint 30) but we
          // still send it here so old backends keep working.
          currency: (form.currency || "USD").toUpperCase(),
          miles: it.miles ? parseFloat(it.miles) : null,
          nights: it.nights ? parseInt(it.nights) : null,
          department_id: it.department_id || null,
          receipt_url: it.receipt_url || null,
        })),
      };
      const url = asDraft ? "/travel?as_draft=true" : "/travel";
      await api.post(url, payload);
      onSaved();
    } catch (err) {
      alert(err?.response?.data?.detail || "Error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-[20px] max-w-[920px] w-full p-6 shadow-xl max-h-[92vh] overflow-y-auto">
        <div className="flex items-start justify-between mb-4">
          <h3 className="sw-heading text-[20px]">{t("travel.new_expense_title")}</h3>
          <button onClick={onClose} className="text-[#747474]" data-testid="close-travel-modal"><X size={20} /></button>
        </div>

        {policy && (
          <div className="sw-card !p-4 mb-4 bg-[#fef3c7] border-[#f59e0b]">
            <div className="flex items-center gap-2 mb-2 text-[12px] uppercase tracking-wider text-[#92400e] font-semibold"><ShieldCheck size={14} /> {policy.name}</div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-[11px]">
              {policy.categories.map((c) => (
                <div key={c.category} className="bg-white rounded-[8px] p-2">
                  <div className="text-[#747474] capitalize">{c.category.replace("_", " ")}</div>
                  <div className="font-semibold text-[#475569]">${c.daily_limit} <span className="text-[10px] text-[#747474]">{t(`travel.${c.unit}`)}</span></div>
                </div>
              ))}
            </div>
          </div>
        )}

        <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <label className="sw-label">{t("travel.purpose")} *</label>
            <input required value={form.purpose} onChange={(e) => setForm({ ...form, purpose: e.target.value })} className="sw-input" data-testid="travel-purpose" />
          </div>
          <div>
            <label className="sw-label">{t("travel.destination")} *</label>
            <input required value={form.destination} onChange={(e) => setForm({ ...form, destination: e.target.value })} className="sw-input" data-testid="travel-destination" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="sw-label">{t("travel.departure")} *</label>
              <input required type="date" value={form.departure_date} onChange={(e) => setForm({ ...form, departure_date: e.target.value })} className="sw-input" data-testid="travel-departure" />
            </div>
            <div>
              <label className="sw-label">{t("travel.return")} *</label>
              <input required type="date" value={form.return_date} onChange={(e) => setForm({ ...form, return_date: e.target.value })} className="sw-input" data-testid="travel-return" />
            </div>
          </div>

          {/* Sprint 30 — one currency for the whole request. Every item is
              recorded and reported in this currency; converted to USD at
              approval time using that day's FX rate. */}
          <div className="md:col-span-2">
            <label className="sw-label">{t("travel.request_currency") || "Currency (applies to all items)"} *</label>
            <div className="max-w-[240px]">
              <CurrencySelect
                value={form.currency || "USD"}
                onChange={(v) => setForm({ ...form, currency: v })}
                testid="travel-request-currency"
              />
            </div>
            <div className="text-[11px] text-[#94a3b8] mt-1">
              {t("travel.request_currency_help") || "All expense items in this request will be recorded in this currency. USD equivalent is locked at approval."}
            </div>
          </div>

          <div className="md:col-span-2">
            <div className="flex items-center justify-between mb-2">
              <label className="sw-label !mb-0">{t("travel.items_with_cost_center")}</label>
              <button type="button" onClick={addItem} className="sw-btn-secondary !py-1 !px-3 !text-[12px]" data-testid="add-travel-item"><Plus size={12} /> {t("common.add")}</button>
            </div>
            <div className="flex flex-col gap-2">
              {items.map((it, idx) => {
                const warning = checkPolicy(it);
                return (
                  <div key={idx} className={`p-3 rounded-[10px] border ${warning ? "bg-[#ffefef] border-[#e8b4b4]" : "bg-[#f6f6f6] border-[#eeeded]"}`}>
                    <div className="grid grid-cols-12 gap-2 items-center">
                      <select value={it.category} onChange={(e) => updateItem(idx, { category: e.target.value })} className="sw-input !py-1.5 col-span-2 !text-[12px]">
                        {CATEGORIES.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                      </select>
                      <input required type="date" value={it.date} onChange={(e) => updateItem(idx, { date: e.target.value })} className="sw-input !py-1.5 col-span-2 !text-[12px]" />
                      <input placeholder={t("travel.description_placeholder")} value={it.description} onChange={(e) => updateItem(idx, { description: e.target.value })} className="sw-input !py-1.5 col-span-3 !text-[12px]" />
                      {it.category === "mileage" && (
                        <input type="number" step="0.1" placeholder={t("travel.miles")} value={it.miles} onChange={(e) => updateItem(idx, { miles: e.target.value })} className="sw-input !py-1.5 col-span-1 !text-[12px]" />
                      )}
                      {it.category === "lodging" && (
                        <input type="number" placeholder={t("travel.nights")} value={it.nights} onChange={(e) => updateItem(idx, { nights: e.target.value })} className="sw-input !py-1.5 col-span-1 !text-[12px]" />
                      )}
                      <input required type="number" step="0.01" placeholder={t("travel.amount")} value={it.amount} onChange={(e) => updateItem(idx, { amount: e.target.value })} className={`sw-input !py-1.5 !text-[12px] ${it.category === "mileage" || it.category === "lodging" ? "col-span-2" : "col-span-3"}`} data-testid={`travel-item-amount-${idx}`} />
                      {items.length > 1 && (
                        <button type="button" onClick={() => removeItem(idx)} className="col-span-1 text-[#881a27] hover:bg-[#e5c1c1] rounded-[8px] p-1.5" data-testid={`travel-item-remove-${idx}`}><Trash2 size={14} /></button>
                      )}
                    </div>
                    {/* FX preview — visible only when currency is non-USD, so
                        the user sees the exact USD number the policy will
                        compare against. Falls back gracefully while the /fx
                        call is still in flight. */}
                    {(() => {
                      const cur = (form.currency || "USD").toUpperCase();
                      const raw = parseFloat(it.amount) || 0;
                      if (cur === "USD" || raw <= 0) return null;
                      const rate = fxRates[fxKey(cur, it.date)];
                      if (rate == null) {
                        return (
                          <div className="mt-1 text-[10.5px] text-[#94a3b8]" data-testid={`travel-item-fx-loading-${idx}`}>
                            {t("travel.fx_loading")}
                          </div>
                        );
                      }
                      const usd = raw * rate;
                      return (
                        <div className="mt-1 text-[10.5px] text-[#475569]" data-testid={`travel-item-fx-${idx}`}>
                          {currencySymbol(cur)}{raw.toFixed(2)} · ≈ <strong>${usd.toFixed(2)} USD</strong>
                          <span className="text-[#94a3b8]"> · {t("travel.fx_rate_label")} {rate.toFixed(4)}</span>
                        </div>
                      );
                    })()}
                    {/* Cost center row */}
                    <div className="mt-2 flex items-center gap-2">
                      <Building2 size={12} className="text-[#94a3b8]" />
                      <span className="text-[10.5px] uppercase tracking-[0.12em] text-[#94a3b8] font-semibold">{t("travel.cost_center")}</span>
                      <select
                        required
                        value={it.department_id || ""}
                        onChange={(e) => updateItem(idx, { department_id: e.target.value })}
                        className="sw-input !py-1 !text-[12px] flex-1"
                        data-testid={`travel-item-cost-center-${idx}`}
                      >
                        <option value="">Selecione…</option>
                        {departments.map((d) => (
                          <option key={d.id} value={d.id}>{d.cost_code} · {d.name}</option>
                        ))}
                      </select>
                    </div>
                    {/* Per-item receipt upload (required to submit) */}
                    <div className="mt-2">
                      <ReceiptUploader
                        value={it.receipt_url || ""}
                        onChange={(url) => updateItem(idx, { receipt_url: url })}
                        testid={`travel-item-receipt-${idx}`}
                        compact
                      />
                      {!it.receipt_url && (
                        <div className="text-[10.5px] text-[#881a27] mt-1 flex items-center gap-1">
                          <AlertTriangle size={11} /> {t("travel.receipt_required")}
                        </div>
                      )}
                    </div>
                    {/* Row-level red background stays so the offending line is
                        visually flagged, but the warning text is deduped and
                        rendered once per category near the totals block below. */}
                  </div>
                );
              })}
            </div>
            {categoryWarnings.length > 0 && (
              <div
                className="mt-3 rounded-[10px] border border-[#e8b4b4] bg-[#fff5f5] p-3"
                data-testid="travel-category-warnings"
              >
                <div className="text-[11px] uppercase tracking-[0.12em] text-[#881a27] font-semibold flex items-center gap-1 mb-1.5">
                  <AlertTriangle size={12} /> {t("travel.policy_alerts") || "Policy alerts"}
                </div>
                <ul className="space-y-1 text-[11.5px] text-[#881a27]">
                  {categoryWarnings.map(([cat, w]) => (
                    <li key={cat} className="flex gap-1.5" data-testid={`travel-category-warning-${cat}`}>
                      <span className="text-[#e8b4b4]">•</span>
                      <span>
                        <strong className="capitalize">{cat.replace("_", " ")}</strong>{" "}
                        <span>{w}</span>
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div className="mt-3 text-right text-[14px]">{t("common.total")}: <span className="font-semibold text-[#475569] text-[18px]">${total.toFixed(2)}</span></div>
          </div>

          <div className="md:col-span-2">
            <label className="sw-label">{t("travel.notes")}</label>
            <textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="sw-input" />
          </div>

          <div className="md:col-span-2 flex justify-end gap-2">
            <button type="button" onClick={onClose} className="sw-btn-secondary">{t("common.cancel")}</button>
            <button
              type="button"
              data-testid="save-draft-travel-btn"
              onClick={() => submit(null, true)}
              disabled={saving}
              className="sw-btn-secondary"
              title={t("travel.save_for_later_hint")}
            >
              {saving ? "..." : t("travel.save_for_later")}
            </button>
            <button data-testid="submit-travel-btn" type="submit" disabled={saving} className="sw-btn-primary">{saving ? "..." : t("travel.submit_for_approval")}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function TravelDetailModal({ travel, user, policy, departments, expenseTypes, onClose, onUpdated }) {
  const { t } = useI18n();
  const CATEGORIES = (expenseTypes && expenseTypes.length > 0) ? expenseTypes : DEFAULT_CATEGORIES;
  const firstKey = CATEGORIES[0]?.key || "other";
  const [working, setWorking] = useState(false);
  const [qboRetrying, setQboRetrying] = useState(false);
  const [comment, setComment] = useState("");
  const [itemizedMode, setItemizedMode] = useState(false);
  const [lineDecisions, setLineDecisions] = useState({});
  const [lastResult, setLastResult] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState({
    purpose: travel.purpose || "",
    destination: travel.destination || "",
    departure_date: travel.departure_date || "",
    return_date: travel.return_date || "",
    notes: travel.notes || "",
    // Sprint 30 — one currency governs the whole draft. Fall back to first item.
    currency: (travel.currency || travel.items?.[0]?.currency || "USD").toUpperCase(),
  });
  const [editItems, setEditItems] = useState(() => (travel.items || []).map((it) => ({
    item_id: it.item_id || "",
    previous_rejection_reason: it.previous_rejection_reason || "",
    category: it.category,
    date: it.date || "",
    description: it.description || "",
    amount: String(it.amount ?? ""),
    miles: it.miles != null ? String(it.miles) : "",
    nights: it.nights != null ? String(it.nights) : "",
    department_id: it.department_id || "",
    receipt_url: it.receipt_url || "",
  })));
  // FX rates for the draft edit view — same pattern as NewTravelModal.
  const [editFxRates, setEditFxRates] = useState({});
  const editFxKey = (cur, dt) => `${(cur || "USD").toUpperCase()}|${(dt || editForm.departure_date || "").slice(0, 10)}`;

  useEffect(() => {
    const cur = (editForm.currency || "USD").toUpperCase();
    if (cur === "USD") return;
    const need = new Set();
    editItems.forEach((it) => {
      const key = editFxKey(cur, it.date);
      if (!(key in editFxRates)) need.add(key);
    });
    if (!need.size) return;
    (async () => {
      const patch = {};
      for (const k of need) {
        const [c, dt] = k.split("|");
        const dtOk = /^\d{4}-\d{2}-\d{2}$/.test(dt) && dt >= "1999-01-01";
        try {
          const q = dtOk ? `?currency=${c}&on_date=${dt}` : `?currency=${c}`;
          const { data } = await api.get(`/fx/rate${q}`);
          patch[k] = data.rate;
        } catch {
          try { const { data } = await api.get(`/fx/rate?currency=${c}`); patch[k] = data.rate; }
          catch { patch[k] = null; }
        }
      }
      setEditFxRates((prev) => ({ ...prev, ...patch }));
    })();
  }, [editItems, editForm.departure_date, editForm.currency]);

  const editUsdOf = (it) => {
    const raw = parseFloat(it.amount) || 0;
    const cur = (editForm.currency || "USD").toUpperCase();
    if (cur === "USD") return raw;
    const rate = editFxRates[editFxKey(cur, it.date)];
    return rate == null ? raw : raw * rate;
  };

  // Mirror of checkPolicy from NewTravelModal, but pointed at editItems /
  // editForm. Extracted so the deduped category-level warning block below the
  // items can call it too.
  const editCheckPolicy = (it) => {
    const rule = policy?.categories?.find((c) => c.category === it.category);
    if (!rule) return null;
    const dl = parseFloat(rule.daily_limit) || 0;
    const mode = rule.enforcement_mode || "per_day";
    const amt = editUsdOf(it);
    if (rule.unit === "per_mile") {
      const miles = parseFloat(it.miles) || 0;
      const expected = miles * dl;
      if (miles > 0 && amt > expected * 1.05) return `Excede ($${expected.toFixed(2)} esperado para ${miles}mi)`;
      return null;
    }
    if (dl <= 0) return null;
    let days = 1;
    if (editForm.departure_date && editForm.return_date) {
      const d1 = new Date(editForm.departure_date), d2 = new Date(editForm.return_date);
      if (!Number.isNaN(d2 - d1)) days = Math.max(1, Math.round((d2 - d1) / 86400000) + 1);
    }
    if (mode === "per_item" && amt > dl) return `Excede $${dl}/item`;
    if (mode === "per_day") {
      const daySum = editItems.filter((x) => x.category === it.category && (x.date || "—") === (it.date || "—"))
        .reduce((s, x) => s + editUsdOf(x), 0);
      if (daySum > dl) return `Excede ${it.category} em ${it.date}: $${daySum.toFixed(2)} > $${dl}/dia`;
    }
    if (mode === "per_trip") {
      const cap = dl * days;
      const total = editItems.filter((x) => x.category === it.category).reduce((s, x) => s + editUsdOf(x), 0);
      if (total > cap) return `Excede ${it.category} no trip: $${total.toFixed(2)} > cap $${cap.toFixed(2)} (${days}d × $${dl})`;
    }
    return null;
  };

  const editCategoryWarnings = (() => {
    const map = new Map();
    editItems.forEach((it) => {
      if (map.has(it.category)) return;
      const w = editCheckPolicy(it);
      if (w) map.set(it.category, w);
    });
    return Array.from(map.entries());
  })();

  const currentRole = travel.required_roles?.[travel.current_step];
  const isSuperApprover = ["hr", "admin", "ceo"].includes(user?.role);
  const canDecide = travel.status === "pending_approval" && (currentRole === user?.role || isSuperApprover);
  const canMarkPaid = travel.status === "ready_for_finance" && ["admin", "hr", "ceo"].includes(user?.role);
  const isDraftOwner = travel.status === "draft" && travel.user_id === user?.id;
  const isAdmin = user?.role === "admin";
  // Sprint 30 — anyone with delete rights: draft-owner OR admin (any status).
  const canDelete = (isDraftOwner || isAdmin) && !travel.deleted_at;

  const deleteRequest = async () => {
    const msg = isAdmin && !isDraftOwner
      ? t("travel.confirm_admin_delete") || "This will move the request to the deleted trash. Only admins can restore it. Continue?"
      : t("travel.confirm_delete") || "Delete this draft?";
    if (!window.confirm(msg)) return;
    setWorking(true);
    try {
      await api.delete(`/travel/${travel.id}`);
      await onUpdated();
    } catch (err) {
      alert(err?.response?.data?.detail || "Error");
    } finally {
      setWorking(false);
    }
  };

  const editTotal = editItems.reduce((s, i) => s + (parseFloat(i.amount) || 0), 0);

  const decide = async (decision) => {
    setWorking(true);
    try {
      await api.post(`/travel/${travel.id}/decide`, { decision, comment });
      await onUpdated();
    } catch (err) {
      alert(err?.response?.data?.detail || "Error");
    } finally {
      setWorking(false);
    }
  };

  const decideItems = async () => {
    // Require a decision for every line item
    const items = travel.items || [];
    const missing = items.filter((it) => !lineDecisions[it.item_id]);
    if (missing.length > 0) {
      alert(t("travel.alert_set_decision_all", { total: items.length, missing: missing.length }));
      return;
    }
    setWorking(true);
    try {
      const payload = {
        decisions: items.map((it) => ({ item_id: it.item_id, decision: lineDecisions[it.item_id], comment })),
        comment,
      };
      const { data } = await api.post(`/travel/${travel.id}/decide-items`, payload);
      setLastResult(data);
      setLineDecisions({});
      await onUpdated();
    } catch (err) {
      alert(err?.response?.data?.detail || "Error");
    } finally {
      setWorking(false);
    }
  };

  const updateEditItem = (idx, patch) => setEditItems(editItems.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  const removeEditItem = (idx) => {
    if (editItems.length === 1) { alert(t("travel.at_least_one_item")); return; }
    setEditItems(editItems.filter((_, i) => i !== idx));
  };
  const addEditItem = () => setEditItems([...editItems, { item_id: "", previous_rejection_reason: "", category: firstKey, date: "", description: "", amount: "", miles: "", nights: "", department_id: departments[0]?.id || "", receipt_url: "" }]);

  const buildEditPayload = () => ({
    ...editForm,
    currency: (editForm.currency || "USD").toUpperCase(),
    items: editItems.map((it) => ({
      item_id: it.item_id || null,
      previous_rejection_reason: it.previous_rejection_reason || null,
      category: it.category,
      date: it.date,
      description: it.description || null,
      amount: parseFloat(it.amount) || 0,
      // Sprint 30 — single currency at the request level; backend re-coerces.
      currency: (editForm.currency || "USD").toUpperCase(),
      miles: it.miles ? parseFloat(it.miles) : null,
      nights: it.nights ? parseInt(it.nights) : null,
      department_id: it.department_id || null,
      receipt_url: it.receipt_url || null,
    })),
  });

  const saveDraft = async () => {
    // Light validation — drafts allow partial data, but we still need date+amount on each
    // item so the backend's policy validation doesn't crash. Cost-center is allowed empty
    // in draft mode (user can fill in before submitting).
    for (const it of editItems) {
      if (!it.date || !it.amount) {
        alert(t("travel.alert_draft_min"));
        return;
      }
    }
    setWorking(true);
    try {
      await api.post(`/travel/${travel.id}/save-draft`, buildEditPayload());
      setEditMode(false);
      await onUpdated();
      onClose();
    } catch (err) {
      alert(err?.response?.data?.detail || t("travel.alert_save_draft_err"));
    } finally {
      setWorking(false);
    }
  };

  const resubmit = async () => {
    // Basic validation: every item must have date + amount + cost-center + receipt
    for (const it of editItems) {
      if (!it.date || !it.amount || !it.department_id) {
        alert(t("travel.alert_resubmit_min"));
        return;
      }
    }
    const missingReceipts = editItems.filter((it) => !it.receipt_url);
    if (missingReceipts.length > 0) {
      alert(t("travel.alert_missing_receipts", { n: missingReceipts.length }));
      return;
    }
    if (!editForm.purpose || !editForm.destination || !editForm.departure_date || !editForm.return_date) {
      alert(t("travel.alert_fill_trip_fields"));
      return;
    }
    setWorking(true);
    try {
      await api.post(`/travel/${travel.id}/resubmit`, buildEditPayload());
      setEditMode(false);
      await onUpdated();
      onClose();
    } catch (err) {
      alert(err?.response?.data?.detail || t("travel.alert_resubmit_err"));
    } finally {
      setWorking(false);
    }
  };

  const markPaid = async () => {
    setWorking(true);
    try {
      await api.post(`/travel/${travel.id}/mark-paid`);
      await onUpdated();
    } catch (err) {
      alert(err?.response?.data?.detail || "Error");
    } finally {
      setWorking(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-[20px] max-w-[820px] w-full p-6 shadow-xl max-h-[92vh] overflow-y-auto">
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-[#475569]">Travel · {travel.user_name}</div>
            <h3 className="sw-heading text-[22px] mt-1">{travel.purpose}</h3>
            <div className="text-[13px] text-[#747474] flex items-center gap-3 mt-1">
              <span className="flex items-center gap-1"><MapPin size={12} /> {travel.destination}</span>
              <span className="flex items-center gap-1"><Calendar size={12} /> {travel.departure_date} → {travel.return_date}</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {statusBadge(travel.status, t)}
            <button
              data-testid={`travel-detail-pdf-${travel.id}`}
              onClick={async () => {
                try {
                  const { data } = await api.get(`/travel/${travel.id}/pdf`, { responseType: "blob" });
                  downloadBlob(data, `travel-${travel.id.slice(0, 8)}.pdf`);
                } catch (e) { alert(e?.response?.data?.detail || t("common.error")); }
              }}
              className="text-[#475569] hover:text-[#1e293b] flex items-center gap-1 text-[12px]"
              title={t("common.download_pdf")}
            >
              <FileText size={14} /> PDF
            </button>
            <button onClick={onClose} className="text-[#747474]"><X size={20} /></button>
          </div>
        </div>

        {travel.originated_from && (
          <div className="rounded-[12px] border border-amber-300 bg-amber-50 p-3 mb-4 text-[12px] text-amber-900" data-testid="travel-originated-banner">
            <div className="flex items-start gap-2">
              <AlertTriangle size={14} className="mt-0.5 shrink-0" />
              <div>
                <div>{t("travel.originated_banner")}</div>
                {travel.originated_rejected_count > 0 && (
                  <div className="mt-0.5 text-[11px] text-amber-800">
                    {t("travel.originated_count_hint", { n: travel.originated_rejected_count })}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {isDraftOwner && (
          <div className="rounded-[12px] border border-amber-300 bg-amber-50 p-3 mb-4 flex items-center justify-between gap-3" data-testid="travel-draft-owner-banner">
            <div className="text-[12px] text-amber-900 flex-1">
              {editMode ? (
                <span>{t("travel.draft_owner_banner_editing")} <strong>{t("travel.resubmit")}</strong>.</span>
              ) : (
                <span>{t("travel.draft_owner_banner_idle")}</span>
              )}
            </div>
            {!editMode ? (
              <button data-testid="travel-edit-draft-btn" onClick={() => setEditMode(true)} className="sw-btn-primary">
                {t("travel.edit_draft")}
              </button>
            ) : (
              <button data-testid="travel-cancel-edit-btn" onClick={() => setEditMode(false)} className="sw-btn-secondary">
                {t("travel.cancel_edit")}
              </button>
            )}
          </div>
        )}

        {/* Sprint 30 — delete action. Draft owners can nuke their own draft;
             admins can soft-delete anything for audit. */}
        {canDelete && !editMode && (
          <div className="mb-4 flex justify-end">
            <button
              data-testid="travel-delete-btn"
              onClick={deleteRequest}
              disabled={working}
              className="inline-flex items-center gap-1.5 rounded-[10px] border border-[#f1c2c2] bg-white px-3 py-1.5 text-[12px] font-semibold text-[#881a27] hover:bg-[#fef2f2] disabled:opacity-50"
              title={isAdmin && !isDraftOwner
                ? (t("travel.admin_delete_hint") || "Admin: delete this request (audit trail preserved)")
                : (t("travel.delete_draft_hint") || "Delete this draft")}
            >
              <Trash2 size={13} />
              {isAdmin && !isDraftOwner
                ? (t("travel.admin_delete_btn") || "Delete (admin)")
                : (t("common.delete") || "Delete")}
            </button>
          </div>
        )}

        {/* Sprint 30 — admin can restore soft-deleted requests. */}
        {travel.deleted_at && isAdmin && (
          <div className="mb-4 rounded-[14px] border border-rose-200 bg-rose-50 p-4" data-testid="travel-deleted-banner">
            <div className="flex items-start gap-3">
              <Trash2 size={16} className="text-rose-700 mt-0.5" />
              <div className="flex-1">
                <div className="text-[12px] uppercase tracking-[0.1em] font-semibold text-rose-900">{t("common.deleted") || "Deleted"}</div>
                <div className="text-[12px] text-rose-800 mt-0.5">
                  {t("travel.deleted_by")}: <strong>{travel.deleted_by_name}</strong> ({travel.deleted_by_role}) · {travel.deleted_at?.slice(0, 16).replace("T", " ")}
                </div>
              </div>
              <button
                data-testid="travel-restore-btn"
                onClick={async () => {
                  try {
                    await api.post(`/travel/${travel.id}/restore`);
                    await onUpdated();
                  } catch (e) { alert(e?.response?.data?.detail || "Error"); }
                }}
                className="inline-flex items-center gap-1.5 rounded-[10px] bg-white border border-rose-300 px-3 py-1.5 text-[12px] font-semibold text-rose-900 hover:bg-rose-100"
              >
                <RotateCcw size={13} /> {t("travel.restore")}
              </button>
            </div>
          </div>
        )}

        {isDraftOwner && editMode && (
          <div className="sw-card !p-4 mb-4" data-testid="travel-edit-form">
            <div className="text-[11px] uppercase tracking-wider text-[#475569] mb-3">{t("travel.edit_form_title")}</div>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <label className="sw-label">{t("travel.purpose")}</label>
                <input data-testid="edit-purpose" value={editForm.purpose} onChange={(e) => setEditForm({ ...editForm, purpose: e.target.value })} className="sw-input" />
              </div>
              <div>
                <label className="sw-label">{t("travel.destination")}</label>
                <input data-testid="edit-destination" value={editForm.destination} onChange={(e) => setEditForm({ ...editForm, destination: e.target.value })} className="sw-input" />
              </div>
              <div>
                <label className="sw-label">{t("travel.departure")}</label>
                <input type="date" data-testid="edit-departure" value={editForm.departure_date} onChange={(e) => setEditForm({ ...editForm, departure_date: e.target.value })} className="sw-input" />
              </div>
              <div>
                <label className="sw-label">{t("travel.return")}</label>
                <input type="date" data-testid="edit-return" value={editForm.return_date} onChange={(e) => setEditForm({ ...editForm, return_date: e.target.value })} className="sw-input" />
              </div>
              <div className="col-span-2">
                <label className="sw-label">{t("travel.notes")}</label>
                <input data-testid="edit-notes" value={editForm.notes} onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })} className="sw-input" placeholder={t("travel.notes_optional")} />
              </div>
              {/* Sprint 30 — request-level currency governs every item. */}
              <div className="col-span-2">
                <label className="sw-label">{t("travel.request_currency") || "Currency (applies to all items)"} *</label>
                <div className="max-w-[240px]">
                  <CurrencySelect
                    value={editForm.currency || "USD"}
                    onChange={(v) => setEditForm({ ...editForm, currency: v })}
                    testid="edit-request-currency"
                  />
                </div>
              </div>
            </div>

            <div className="text-[11px] uppercase tracking-wider text-[#475569] mb-2 mt-4">{t("travel.items_count", { n: editItems.length })}</div>
            <div className="flex flex-col gap-2">
              {editItems.map((it, idx) => {
                const editWarning = editCheckPolicy(it);
                return (
                <div key={idx} className={`border rounded-[10px] p-3 ${editWarning ? "border-[#e8b4b4] bg-[#fff5f5]" : "border-[#eeeded] bg-[#fafafa]"}`} data-testid={`edit-item-${idx}`}>
                  {it.previous_rejection_reason && (
                    <div className="mb-2 rounded-[8px] border border-rose-200 bg-rose-50 px-2.5 py-1.5 text-[11px] text-rose-800 flex items-start gap-1.5" data-testid={`edit-item-rejection-${idx}`}>
                      <AlertTriangle size={12} className="mt-0.5 shrink-0" />
                      <div>
                        <span className="font-semibold uppercase tracking-[0.1em] text-[10px] block mb-0.5">{t("travel.previous_rejection")}</span>
                        <span className="italic">&ldquo;{it.previous_rejection_reason}&rdquo;</span>
                      </div>
                    </div>
                  )}
                  <div className="grid grid-cols-12 gap-2 items-end">
                    <div className="col-span-3">
                      <label className="sw-label">{t("travel.category")}</label>
                      <select value={it.category} onChange={(e) => updateEditItem(idx, { category: e.target.value })} className="sw-input !py-1.5 !text-[12px]">
                        {CATEGORIES.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                      </select>
                    </div>
                    <div className="col-span-3">
                      <label className="sw-label">{t("travel.date")}</label>
                      <input type="date" value={it.date} onChange={(e) => updateEditItem(idx, { date: e.target.value })} className="sw-input !py-1.5 !text-[12px]" />
                    </div>
                    <div className="col-span-6">
                      <label className="sw-label">{t("travel.description")}</label>
                      <input value={it.description} onChange={(e) => updateEditItem(idx, { description: e.target.value })} className="sw-input !py-1.5 !text-[12px]" placeholder={t("travel.description_opt")} />
                    </div>
                  </div>
                  {/* Second row — give the amount field a lot of breathing room. */}
                  <div className="grid grid-cols-12 gap-2 items-end mt-2">
                    <div className="col-span-4">
                      <label className="sw-label">{t("travel.amount")}</label>
                      <input
                        type="number"
                        step="0.01"
                        value={it.amount}
                        onChange={(e) => updateEditItem(idx, { amount: e.target.value })}
                        className="sw-input !py-2 !text-[15px] font-semibold text-right"
                        data-testid={`edit-item-amount-${idx}`}
                      />
                    </div>
                    {it.category === "mileage" && (
                      <div className="col-span-3">
                        <label className="sw-label">{t("travel.miles")}</label>
                        <input type="number" step="0.1" value={it.miles || ""} onChange={(e) => updateEditItem(idx, { miles: e.target.value })} className="sw-input !py-2 !text-[12px]" />
                      </div>
                    )}
                    {it.category === "lodging" && (
                      <div className="col-span-3">
                        <label className="sw-label">{t("travel.nights")}</label>
                        <input type="number" value={it.nights || ""} onChange={(e) => updateEditItem(idx, { nights: e.target.value })} className="sw-input !py-2 !text-[12px]" />
                      </div>
                    )}
                    <div className={`flex items-end justify-end ${(it.category === "mileage" || it.category === "lodging") ? "col-span-5" : "col-span-8"}`}>
                      <button type="button" onClick={() => removeEditItem(idx)} className="text-[#881a27] hover:bg-[#fee2e2] p-2 rounded" data-testid={`edit-item-remove-${idx}`}>
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                  {/* FX preview — uses request-level currency now. */}
                  {(() => {
                    const cur = (editForm.currency || "USD").toUpperCase();
                    const raw = parseFloat(it.amount) || 0;
                    if (cur === "USD" || raw <= 0) return null;
                    const rate = editFxRates[editFxKey(cur, it.date)];
                    if (rate == null) return <div className="mt-1 text-[10.5px] text-[#94a3b8]" data-testid={`edit-item-fx-loading-${idx}`}>{t("travel.fx_loading")}</div>;
                    return (
                      <div className="mt-1 text-[10.5px] text-[#475569]" data-testid={`edit-item-fx-${idx}`}>
                        {currencySymbol(cur)}{raw.toFixed(2)} · ≈ <strong>${(raw * rate).toFixed(2)} USD</strong>
                        <span className="text-[#94a3b8]"> · {t("travel.fx_rate_label")} {rate.toFixed(4)}</span>
                      </div>
                    );
                  })()}
                  <div className="mt-2 flex items-center gap-2">
                    <Building2 size={12} className="text-[#94a3b8]" />
                    <span className="text-[10.5px] uppercase tracking-[0.12em] text-[#94a3b8] font-semibold">{t("travel.cost_center")}</span>
                    <select
                      value={it.department_id || ""}
                      onChange={(e) => updateEditItem(idx, { department_id: e.target.value })}
                      className="sw-input !py-1 !text-[12px] flex-1"
                    >
                      <option value="">{t("travel.select_cost_center")}</option>
                      {departments.map((d) => (
                        <option key={d.id} value={d.id}>{d.cost_code} · {d.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="mt-2">
                    <ReceiptUploader
                      value={it.receipt_url || ""}
                      onChange={(url) => updateEditItem(idx, { receipt_url: url })}
                      testid={`edit-item-receipt-${idx}`}
                      compact
                    />
                    {!it.receipt_url && (
                      <div className="text-[10.5px] text-[#881a27] mt-1 flex items-center gap-1">
                        <AlertTriangle size={11} /> {t("travel.receipt_required")}
                      </div>
                    )}
                  </div>
                  {/* Warning text moved to a deduped block below the items list */}
                </div>
              );
              })}
            </div>
            {editCategoryWarnings.length > 0 && (
              <div
                className="mt-3 rounded-[10px] border border-[#e8b4b4] bg-[#fff5f5] p-3"
                data-testid="travel-edit-category-warnings"
              >
                <div className="text-[11px] uppercase tracking-[0.12em] text-[#881a27] font-semibold flex items-center gap-1 mb-1.5">
                  <AlertTriangle size={12} /> {t("travel.policy_alerts") || "Policy alerts"}
                </div>
                <ul className="space-y-1 text-[11.5px] text-[#881a27]">
                  {editCategoryWarnings.map(([cat, w]) => (
                    <li key={cat} className="flex gap-1.5" data-testid={`travel-edit-category-warning-${cat}`}>
                      <span className="text-[#e8b4b4]">•</span>
                      <span>
                        <strong className="capitalize">{cat.replace("_", " ")}</strong> <span>{w}</span>
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="mt-3 flex items-center justify-between">
              <button type="button" onClick={addEditItem} className="sw-btn-secondary !py-1.5 !text-[12px]" data-testid="edit-add-item">
                <Plus size={12} /> {t("reimb.add_item")}
              </button>
              <div className="text-[13px] font-semibold text-[#475569]">{t("travel.new_total", { total: editTotal.toFixed(2) })}</div>
            </div>

            <div className="mt-4 flex justify-end gap-2 pt-3 border-t border-[#eeeded]">
              <button onClick={() => setEditMode(false)} className="sw-btn-secondary">{t("common.cancel")}</button>
              <button
                type="button"
                data-testid="travel-save-draft-btn"
                onClick={saveDraft}
                disabled={working}
                className="sw-btn-secondary"
                title={t("travel.save_for_later_hint")}
              >
                {working ? "..." : t("travel.save_for_later")}
              </button>
              <button data-testid="travel-resubmit-btn" onClick={resubmit} disabled={working} className="sw-btn-primary disabled:opacity-50">
                {working ? t("travel.resubmitting") : t("travel.resubmit")}
              </button>
            </div>
          </div>
        )}

        {travel.compliance_flagged && (
          <div className="rounded-[14px] border border-rose-200 bg-rose-50 p-4 mb-4 flex gap-3">
            <div className="w-8 h-8 rounded-full bg-rose-100 text-rose-700 flex items-center justify-center shrink-0">
              <AlertTriangle size={16} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[13px] font-semibold text-rose-800">
                {travel.flagged_count} {t("travel.exceed_policy")} {travel.policy_name && <span className="font-normal text-rose-600">· {travel.policy_name}</span>}
              </div>
              <ul className="mt-2 space-y-1 text-[12px] text-rose-700">
                {travel.items?.filter((it) => it.exceeds_policy).map((it, idx) => {
                  const cat = policy?.categories?.find((c) => c.category === it.category);
                  const unit = cat?.unit || "";
                  const limit = it.policy_limit ?? cat?.daily_limit ?? 0;
                  let reason = `Above limit ($${limit.toFixed ? limit.toFixed(2) : limit} ${unit.replace("_", " ")})`;
                  if (it.category === "mileage" && it.miles) {
                    reason = `${it.miles}mi × $${cat?.daily_limit}/mi = $${limit.toFixed ? limit.toFixed(2) : limit} expected, logged $${it.amount?.toFixed(2)}`;
                  }
                  return (
                    <li key={idx} className="flex gap-2">
                      <span className="text-rose-400">•</span>
                      <span><strong className="capitalize text-rose-800">{it.category.replace("_", " ")}</strong> on {it.date}: ${it.amount?.toFixed(2)} — {reason}</span>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        )}

        {!editMode && (
        <div className="sw-card !p-4 mb-4">
          <div className="text-[11px] uppercase tracking-wider text-[#475569] mb-3">{t("travel.expense_items")}</div>
          <div className="flex flex-col gap-2">
            {travel.items?.map((it, i) => {
              const dept = departments?.find((d) => d.id === it.department_id);
              const lineDecision = lineDecisions[it.item_id];
              const showLineDecision = itemizedMode && canDecide && it.item_id;
              return (
                <div key={i} className={`p-2 rounded-[8px] text-[12px] ${lineDecision === "approved" ? "bg-[#f0fdf4] border border-[#86efac]" : lineDecision === "rejected" ? "bg-[#fef2f2] border border-[#fca5a5]" : it.exceeds_policy ? "bg-[#ffefef]" : "bg-[#f6f6f6]"}`} data-testid={`travel-item-row-${i}`}>
                  <div className="grid grid-cols-12 gap-2 items-center">
                    <span className="col-span-2 capitalize font-medium">{it.category.replace("_", " ")}</span>
                    <span className="col-span-2 text-[#747474]">{it.date}</span>
                    <span className="col-span-3 text-[#323232]">{it.description || "—"} {it.miles ? `· ${it.miles}mi` : ""}{it.nights ? `· ${it.nights}n` : ""}</span>
                    <span className="col-span-2 text-[#475569] text-[11px] truncate flex items-center gap-1" title={dept?.name || ""}>
                      <Building2 size={10} className="text-[#94a3b8] shrink-0" />
                      {dept ? (dept.cost_code || dept.name) : "—"}
                    </span>
                    <span className="col-span-1 text-right font-semibold">
                      {currencySymbol(it.currency)}{it.amount?.toFixed(2)}
                      {it.currency && it.currency !== "USD" && it.usd_amount != null && (
                        <div className="text-[9.5px] text-[#94a3b8] font-normal">≈ ${it.usd_amount.toFixed(2)}</div>
                      )}
                    </span>
                    <span className="col-span-1 text-center">
                      {it.receipt_url ? (
                        <button type="button" onClick={(e) => { e.stopPropagation(); openReceipt(it.receipt_url); }} className="text-[#475569] hover:underline inline-flex items-center" title={t("travel.view_receipt")} data-testid={`travel-item-receipt-link-${i}`}>
                          <Paperclip size={12} />
                        </button>
                      ) : <span className="text-[#cbd5e1]">—</span>}
                    </span>
                    <span className="col-span-1 text-center">{it.exceeds_policy ? <AlertTriangle size={14} className="text-[#881a27]" /> : <CheckCircle2 size={14} className="text-[#008932]" />}</span>
                  </div>
                  {showLineDecision && (
                    <div className="mt-2 flex gap-1.5 justify-end">
                      <button
                        data-testid={`travel-line-approve-${i}`}
                        onClick={() => setLineDecisions((d) => ({ ...d, [it.item_id]: "approved" }))}
                        className={`px-2.5 py-1 rounded-[8px] text-[11px] font-medium transition ${lineDecision === "approved" ? "bg-[#008932] text-white" : "bg-white border border-[#d1d5db] text-[#475569] hover:border-[#008932]"}`}
                      >
                        {t("travel.line_approve")}
                      </button>
                      <button
                        data-testid={`travel-line-reject-${i}`}
                        onClick={() => setLineDecisions((d) => ({ ...d, [it.item_id]: "rejected" }))}
                        className={`px-2.5 py-1 rounded-[8px] text-[11px] font-medium transition ${lineDecision === "rejected" ? "bg-[#881a27] text-white" : "bg-white border border-[#d1d5db] text-[#475569] hover:border-[#881a27]"}`}
                      >
                        {t("travel.line_reject")}
                      </button>
                    </div>
                  )}
                  {it.line_status && (
                    <div className={`mt-1 text-[10px] uppercase tracking-wider font-semibold ${it.line_status === "approved" ? "text-[#008932]" : "text-[#881a27]"}`}>
                      {it.line_status === "approved" ? t("travel.line_approved") : t("travel.line_rejected")}{it.line_comment ? ` · ${it.line_comment}` : ""}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <div className="mt-3 text-right font-semibold text-[#475569]" data-testid="travel-totals-block">
            {(() => {
              // Sprint 30 — Single source of truth for the USD total is the value
              // locked at approval time (`total_usd_amount`). Before approval the
              // `total_amount` field still holds the submit-time USD estimate, so
              // we prefer whichever is defined. We ALSO surface per-currency
              // original sums (BRL / EUR / …) so bookkeepers can cross-check.
              const lockedUsd = travel.total_usd_amount ?? travel.total_amount ?? 0;
              const byCurrency = {};
              (travel.items || []).forEach((it) => {
                const cur = (it.currency || "USD").toUpperCase();
                byCurrency[cur] = (byCurrency[cur] || 0) + (parseFloat(it.amount) || 0);
              });
              const nonUsd = Object.entries(byCurrency).filter(([c]) => c !== "USD");
              const fmt = (v, cur) => new Intl.NumberFormat(cur === "BRL" ? "pt-BR" : "en-US", {
                minimumFractionDigits: 2, maximumFractionDigits: 2,
              }).format(v);
              return (
                <>
                  <div>
                    {t("common.total")}: <span data-testid="travel-total-usd">USD ${fmt(lockedUsd, "USD")}</span>
                    {travel.fx_locked_at && (
                      <span className="ml-2 text-[10px] font-normal text-[#94a3b8] uppercase tracking-wider">
                        {t("travel.fx_locked") || "FX locked"}
                      </span>
                    )}
                  </div>
                  {nonUsd.length > 0 && (
                    <div className="mt-1 text-[11px] font-normal text-[#94a3b8]" data-testid="travel-totals-original">
                      {t("travel.original") || "Original"}:{" "}
                      {nonUsd.map(([cur, v], i) => (
                        <span key={cur}>
                          {i > 0 ? " · " : ""}
                          {cur} {fmt(v, cur)}
                        </span>
                      ))}
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        </div>
        )}

        {/* QBO sync block — Sprint 20 */}
        {travel.qbo_refs?.sync_status && (
          <div className="sw-card !p-4 mb-4 border-[#cbd5e1] bg-[#f8fafc]" data-testid="travel-qbo-block">
            <div className="flex items-baseline justify-between mb-3">
              <div className="text-[11px] uppercase tracking-wider text-[#475569] font-semibold flex items-center gap-2">
                <ReceiptIcon size={12} /> {t("travel.qbo_sync_title")}
              </div>
              {travel.qbo_refs.sync_status === "synced" && (
                <span className={`sw-badge ${travel.qbo_refs.payment_status === "paid" ? "sw-badge-approved" : "sw-badge-pending"}`}>
                  {travel.qbo_refs.payment_status === "paid" ? t("travel.paid_qbo") : travel.qbo_refs.payment_status === "partial" ? t("travel.partial") : t("travel.awaiting_payment")}
                </span>
              )}
              {travel.qbo_refs.sync_status === "error" && <span className="sw-badge sw-badge-rejected">{t("travel.sync_error")}</span>}
            </div>
            {travel.qbo_refs.sync_status === "synced" && (
              <>
                <div className="text-[12.5px] grid grid-cols-2 gap-x-3 gap-y-1.5">
                  <div><span className="text-[#94a3b8] text-[10px] uppercase tracking-wider block">{t("travel.bill_employee")}</span><code className="text-[11.5px] text-[#1e293b]">{travel.qbo_refs.employee_bill_id}</code></div>
                  <div><span className="text-[#94a3b8] text-[10px] uppercase tracking-wider block">{t("travel.employee_vendor")}</span><code className="text-[11.5px] text-[#1e293b]">{travel.qbo_refs.employee_vendor_qbo_id}</code></div>
                  <div><span className="text-[#94a3b8] text-[10px] uppercase tracking-wider block">{t("travel.lines")}</span>{travel.qbo_refs.line_count}</div>
                  {travel.qbo_refs.synced_at && <div><span className="text-[#94a3b8] text-[10px] uppercase tracking-wider block">{t("travel.synced_at")}</span>{travel.qbo_refs.synced_at.slice(0,16).replace("T"," ")}</div>}
                  {travel.qbo_refs.last_payment_check_at && <div className="col-span-2"><span className="text-[#94a3b8] text-[10px] uppercase tracking-wider block">{t("common.last_payment_check")}</span>{travel.qbo_refs.last_payment_check_at.slice(0,16).replace("T"," ")}</div>}
                </div>
                {["hr", "admin"].includes(user?.role) && travel.qbo_refs.payment_status !== "paid" && travel.qbo_refs.payment_status !== "partial" && (
                  <div className="mt-3 pt-3 border-t border-[#e5e7eb] flex justify-end gap-2 flex-wrap">
                    {/* Sprint 30 — push local adjustments to QBO without a full
                        reopen cycle. Deletes the old Bill and recreates it. */}
                    <button
                      data-testid={`travel-qbo-update-${travel.id}`}
                      disabled={qboRetrying}
                      onClick={async () => {
                        if (!window.confirm(t("travel.qbo_update_confirm") || "This will delete the current QBO Bill and recreate it with the latest data (descriptions, totals, FX). Continue?")) return;
                        setQboRetrying(true);
                        try {
                          await api.post(`/travel/${travel.id}/qbo-update`);
                          let settled = false;
                          for (let i = 0; i < 40; i++) {
                            await new Promise((r) => setTimeout(r, 3000));
                            const { data } = await api.get(`/travel/${travel.id}/qbo-status`);
                            if (data.sync_status === "synced") {
                              alert(t("travel.qbo_update_ok") || "QBO Bill refreshed with latest data.");
                              settled = true;
                              break;
                            }
                            if (data.sync_status === "error") {
                              alert(data.last_error || t("common.error"));
                              settled = true;
                              break;
                            }
                          }
                          if (!settled) alert(t("travel.qbo_resync_bg"));
                          await onUpdated();
                        } catch (e) {
                          alert(e?.response?.data?.detail || t("common.error"));
                        } finally { setQboRetrying(false); }
                      }}
                      className="sw-btn-secondary text-[12px] flex items-center gap-1.5"
                      title={t("travel.qbo_update_hint") || "Push local adjustments (descriptions/FX) to QBO"}
                    >
                      <RefreshCw size={13} className={qboRetrying ? "animate-spin" : ""} />
                      {qboRetrying ? "…" : (t("travel.qbo_update_btn") || "Update QBO")}
                    </button>
                    <button
                      data-testid={`travel-qbo-reopen-${travel.id}`}
                      disabled={qboRetrying}
                      onClick={async () => {
                        if (!window.confirm(t("travel.reopen_confirm"))) return;
                        setQboRetrying(true);
                        try {
                          await api.post(`/travel/${travel.id}/reopen`);
                          alert(t("travel.reopen_ok"));
                          await onUpdated();
                          onClose();
                        } catch (e) {
                          alert(e?.response?.data?.detail || t("common.error"));
                        } finally { setQboRetrying(false); }
                      }}
                      className="sw-btn-secondary text-[12px] flex items-center gap-1.5 !text-[#881a27] !border-[#e8b4b4]"
                      title={t("travel.reopen_hint")}
                    >
                      <RefreshCw size={13} className={qboRetrying ? "animate-spin" : ""} />
                      {qboRetrying ? "…" : t("travel.reopen_from_qbo")}
                    </button>
                  </div>
                )}
              </>
            )}
            {travel.qbo_refs.sync_status === "error" && (
              <>
                <div className="text-[12px] text-[#881a27] mt-1 break-words font-mono bg-rose-50 p-2 rounded">{travel.qbo_refs.last_error}</div>
                {["hr", "admin"].includes(user?.role) && (
                  <div className="mt-2 flex justify-end">
                    <button
                      data-testid={`travel-qbo-retry-${travel.id}`}
                      disabled={qboRetrying}
                      onClick={async () => {
                        setQboRetrying(true);
                        try {
                          await api.post(`/travel/${travel.id}/qbo-resync`);
                          let settled = false;
                          for (let i = 0; i < 40; i++) {
                            await new Promise((r) => setTimeout(r, 3000));
                            const { data } = await api.get(`/travel/${travel.id}/qbo-status`);
                            if (data.sync_status === "synced") {
                              alert(t("travel.qbo_resync_ok"));
                              settled = true;
                              break;
                            }
                            if (data.sync_status === "error") {
                              alert(data.last_error || t("common.error"));
                              settled = true;
                              break;
                            }
                          }
                          if (!settled) alert(t("travel.qbo_resync_bg"));
                          await onUpdated();
                        } catch (e) {
                          alert(e?.response?.data?.detail || t("common.error"));
                        } finally { setQboRetrying(false); }
                      }}
                      className="sw-btn-secondary text-[12px] flex items-center gap-1.5"
                    >
                      <RefreshCw size={13} className={qboRetrying ? "animate-spin" : ""} />
                      {qboRetrying ? t("travel.qbo_resync_running") : t("travel.qbo_retry_sync")}
                    </button>
                  </div>
                )}
              </>
            )}
            {(travel.qbo_refs.employee_bill_id || "").startsWith("DRYRUN-") && (
              <div className="text-[10.5px] text-[#a38d5b] mt-3 uppercase tracking-[0.12em] font-semibold">{t("travel.dry_run_note")}</div>
            )}
          </div>
        )}

        <div className="sw-card !p-4 mb-4">
          <div className="text-[11px] uppercase tracking-wider text-[#475569] mb-3">{t("travel.approval_chain")}</div>
          <div className="flex flex-wrap gap-2 items-center">
            {travel.required_roles?.map((r, i) => {
              const ap = travel.approvals?.find((a) => a.step === i);
              const done = !!ap;
              const isCurrent = i === travel.current_step && travel.status === "pending_approval";
              // For step 0, prefer the specifically-assigned approver's name
              // (e.g. "Rodrigo Hipólito") over the generic role label ("manager").
              // Falls back to the role when no specific approver is stamped.
              const chipLabel = (i === 0 && travel.approver_name) ? travel.approver_name : r;
              return (
                <div key={i} data-testid={`travel-chain-step-${i}`} className={`flex items-center gap-2 px-3 py-1.5 rounded-[10px] text-[12px] ${done ? (ap.decision === "approved" ? "bg-[#b8e6cd] text-[#0b430b]" : "bg-[#e5c1c1] text-[#881a27]") : isCurrent ? "bg-[#fef3c7] text-[#92400e]" : "bg-[#f6f6f6] text-[#747474]"}`}>
                  <span className={`${(i === 0 && travel.approver_name) ? "" : "capitalize"} font-medium`}>{chipLabel}</span>
                  {done && <span className="text-[10px]">· {ap.user_name}</span>}
                </div>
              );
            })}
            {travel.status === "ready_for_finance" && <span className="px-3 py-1.5 rounded-[10px] text-[12px] bg-[#e2e8f0] text-[#475569] flex items-center gap-1"><DollarSign size={12} /> Finance handoff</span>}
            {travel.status === "paid" && <span className="px-3 py-1.5 rounded-[10px] text-[12px] bg-[#008932] text-white">PAID</span>}
          </div>
        </div>

        {canDecide && (
          <div className="sw-card !p-4 mb-4 bg-[#fef3c7] border-[#f59e0b]">
            <div className="flex items-center justify-between mb-2">
              <div className="font-medium text-[13px] text-[#92400e]">{t("travel.your_approval", { role: currentRole })}</div>
              <label className="flex items-center gap-1.5 text-[11px] text-[#92400e] cursor-pointer">
                <input
                  type="checkbox"
                  checked={itemizedMode}
                  onChange={(e) => { setItemizedMode(e.target.checked); setLineDecisions({}); }}
                  data-testid="travel-itemized-toggle"
                  className="accent-[#92400e]"
                />
                <ListChecks size={12} /> {t("travel.itemized_toggle")}
              </label>
            </div>
            <input placeholder={t("travel.comment_optional")} value={comment} onChange={(e) => setComment(e.target.value)} className="sw-input mb-2" />
            {itemizedMode ? (
              <div>
                <div className="text-[11px] text-[#92400e] mb-2">
                  {t("travel.itemized_help_1")} <strong>{t("travel.line_approve")}</strong> {t("travel.itemized_help_2")} <strong>{t("travel.line_reject")}</strong> {t("travel.itemized_help_3")}
                </div>
                <div className="text-[11px] text-[#92400e] mb-2">
                  {t("travel.decided_count", { n: Object.keys(lineDecisions).length, total: travel.items?.length || 0 })}
                </div>
                <div className="flex justify-end gap-2">
                  <button
                    data-testid="travel-decide-items-btn"
                    onClick={decideItems}
                    disabled={working || Object.keys(lineDecisions).length === 0}
                    className="sw-btn-primary disabled:opacity-50"
                  >
                    {working ? "..." : t("travel.confirm_decisions", { n: Object.keys(lineDecisions).length })}
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex justify-end gap-2">
                <button data-testid="travel-reject-btn" onClick={() => decide("rejected")} disabled={working} className="sw-btn-secondary !text-[#881a27]">{t("travel.reject")}</button>
                <button data-testid="travel-approve-btn" onClick={() => decide("approved")} disabled={working} className="sw-btn-primary !bg-[#008932] hover:!bg-[#0b430b]">{t("travel.approve")}</button>
              </div>
            )}
            {lastResult && (
              <div className="mt-3 p-3 rounded-[10px] bg-white border border-[#fde68a] text-[11.5px] text-[#1e293b]" data-testid="travel-decide-items-result">
                <div className="font-semibold mb-1">{t("batch.processed_title")}</div>
                <div>{t("travel.decided_count_drafts", { n: lastResult.approved_count, r: lastResult.rejected_count })}</div>
                {lastResult.spawned_draft_id && (
                  <div className="mt-1 text-[#92400e]">{t("travel.spawned_draft")} <code className="text-[10.5px]">{lastResult.spawned_draft_id.slice(0,8)}</code></div>
                )}
              </div>
            )}
          </div>
        )}

        {canMarkPaid && (
          <div className="flex justify-end">
            <button data-testid="travel-mark-paid-btn" onClick={markPaid} disabled={working} className="sw-btn-primary !bg-[#0b430b]"><DollarSign size={14} /> Mark as paid</button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function Travel() {
  const { user } = useAuth();
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  const [policy, setPolicy] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [expenseTypes, setExpenseTypes] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [detail, setDetail] = useState(null);
  const [filterStatus, setFilterStatus] = useState("all");
  const [scope, setScope] = useState(user?.role === "employee" ? "mine" : "all");
  // Sprint 30 — admin-only "Trash" view for soft-deleted requests.
  const [showTrash, setShowTrash] = useState(false);

  const load = async () => {
    if (showTrash && user?.role === "admin") {
      const [tr, p, d, et] = await Promise.all([
        api.get(`/travel/deleted`),
        api.get("/travel-policies/active").catch(() => ({ data: null })),
        api.get("/departments"),
        api.get("/travel-expense-types").catch(() => ({ data: [] })),
      ]);
      setItems(tr.data);
      setPolicy(p.data);
      setDepartments(d.data || []);
      setExpenseTypes(et.data || []);
      return;
    }
    const params = new URLSearchParams();
    if (filterStatus !== "all") params.set("status", filterStatus);
    if (scope === "mine") params.set("mine", "true");
    const [tr, p, d, et] = await Promise.all([
      api.get(`/travel?${params.toString()}`),
      api.get("/travel-policies/active").catch(() => ({ data: null })),
      api.get("/departments"),
      api.get("/travel-expense-types").catch(() => ({ data: [] })),
    ]);
    setItems(tr.data);
    setPolicy(p.data);
    setDepartments(d.data || []);
    setExpenseTypes(et.data || []);
  };
  useEffect(() => { load(); }, [filterStatus, scope, showTrash]); // eslint-disable-line

  const isStaff = ["manager", "hr", "ceo", "admin"].includes(user?.role);

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-[14px] bg-[#e2e8f0] text-[#475569] flex items-center justify-center"><Plane size={22} /></div>
          <div>
            <h1 className="sw-heading text-[28px]">{t("travel.page_title")}</h1>
            <p className="text-[14px] text-[#747474]">{t("travel.requests_policy", { n: items.length })} {policy?.name || "—"}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="travel-export-csv"
            onClick={() => {
              const rows = items.map((r) => ({
                Date: r.created_at?.slice(0, 10) || "",
                User: r.user_name || "",
                Purpose: r.purpose || "",
                Destination: r.destination || "",
                "Departure": r.departure_date || "",
                "Return": r.return_date || "",
                "Total amount": r.total_amount,
                "USD total": r.total_usd_amount ?? "",
                Items: (r.items || []).length,
                Flagged: r.compliance_flagged ? "yes" : "no",
                Status: r.status,
              }));
              exportCsv(`travel_${new Date().toISOString().slice(0, 10)}.csv`, rows);
            }}
            className="sw-btn-secondary"
          >
            <Download size={14} /> {t("common.export_csv")}
          </button>
          <button
            data-testid="travel-export-pdf-bulk"
            onClick={async () => {
              try {
                const { data } = await api.get(`/travel/pdf/bulk`, { responseType: "blob" });
                downloadBlob(data, `travel_${new Date().toISOString().slice(0, 10)}.pdf`);
              } catch (e) { alert(e?.response?.data?.detail || t("common.error")); }
            }}
            className="sw-btn-secondary"
          >
            <FileText size={14} /> {t("common.export_pdf")}
          </button>
          <button data-testid="new-travel-btn" onClick={() => setShowNew(true)} className="sw-btn-primary"><Plus size={14} /> {t("travel.new_expense")}</button>
        </div>
      </div>

      {policy && (
        <div className="sw-card !p-4 bg-[#fef3c7] border-[#f59e0b]">
          <div className="flex items-center gap-2 mb-3 text-[12px] uppercase tracking-wider text-[#92400e] font-semibold"><ShieldCheck size={14} /> {policy.name}</div>
          <div className="grid grid-cols-2 md:grid-cols-6 gap-2 text-[11px]">
            {policy.categories.map((c) => (
              <div key={c.category} className="bg-white rounded-[8px] p-2">
                <div className="text-[#747474] capitalize">{c.category.replace("_", " ")}</div>
                <div className="font-semibold text-[#475569]">${c.daily_limit} <span className="text-[10px] text-[#747474]">{t(`travel.${c.unit}`)}</span></div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex items-center gap-2 flex-wrap">
        {isStaff && !showTrash && (
          <div className="flex gap-1 mr-2">
            <button onClick={() => setScope("mine")} className={`px-3 py-1.5 rounded-[10px] text-[12px] ${scope === "mine" ? "bg-[#475569] text-white" : "bg-white border border-[#eeeded] text-[#747474]"}`}>{t("travel.scope_mine")}</button>
            <button onClick={() => setScope("all")} className={`px-3 py-1.5 rounded-[10px] text-[12px] ${scope === "all" ? "bg-[#475569] text-white" : "bg-white border border-[#eeeded] text-[#747474]"}`}>{t("travel.scope_all")}</button>
          </div>
        )}
        {!showTrash && ["all", "draft", "pending_approval", "ready_for_finance", "submitted_to_qbo", "paid", "rejected"].map((s) => (
          <button key={s} onClick={() => setFilterStatus(s)} className={`px-3 py-1.5 rounded-[10px] text-[11px] font-medium ${filterStatus === s ? "bg-[#475569] text-white" : "bg-white text-[#747474] border border-[#eeeded]"}`}>{s === "all" ? t("common.all") : t(`status.${s}`)}</button>
        ))}
        {user?.role === "admin" && (
          <button
            data-testid="travel-toggle-trash"
            onClick={() => setShowTrash((v) => !v)}
            className={`px-3 py-1.5 rounded-[10px] text-[11px] font-semibold inline-flex items-center gap-1 ${showTrash ? "bg-[#881a27] text-white" : "bg-white text-[#881a27] border border-[#f1c2c2]"}`}
          >
            <Trash2 size={12} /> {t("travel.show_deleted")}
          </button>
        )}
      </div>

      {items.length === 0 ? (
        <div className="sw-card text-center py-12">
          <Plane className="mx-auto mb-3 text-[#cbd5e1]" size={36} />
          <div className="text-[14px] text-[#747474] mb-1">{t("travel.empty_title")}</div>
          <div className="text-[12px] text-[#94a3b8] mb-5">{t("travel.empty_hint")}</div>
          <button data-testid="new-travel-empty-btn" onClick={() => setShowNew(true)} className="sw-btn-primary">
            <Plus size={14} /> {t("travel.create_expense")}
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {items.map((tr) => (
            <button key={tr.id} data-testid={`travel-card-${tr.id}`} onClick={() => setDetail(tr)} className={`sw-card text-left hover:border-[#475569] ${tr.originated_from ? "border-amber-300 bg-amber-50/40" : ""}`}>
              <div className="flex items-start justify-between mb-2">
                <div>
                  <div className="text-[11px] uppercase tracking-wider text-[#475569]">{tr.user_name}</div>
                  <div className="font-medium text-[16px] text-[#323232] mt-1">{tr.purpose}</div>
                  <div className="text-[12px] text-[#747474] flex items-center gap-2 mt-1"><MapPin size={11} /> {tr.destination} · {tr.departure_date}</div>
                  {tr.originated_from && (
                    <div className="mt-2 inline-flex items-center gap-1 text-[10.5px] font-semibold uppercase tracking-[0.1em] text-amber-800 bg-amber-100 border border-amber-200 rounded-full px-2 py-0.5" data-testid={`travel-card-spawned-${tr.id}`}>
                      <AlertTriangle size={10} /> {t("travel.spawned_card_badge")}
                    </div>
                  )}
                </div>
                <div className="flex flex-col items-end gap-1">
                  {statusBadge(tr.status, t)}
                  {tr.status === "pending_approval" && tr.approver_name && (
                    <span data-testid="travel-pending-approver" className="text-[10px] text-[#92400e]">
                      {t("travel.waiting_for")} <strong>{tr.approver_name}</strong>
                    </span>
                  )}
                  {tr.compliance_flagged && <span className="text-[10px] text-[#881a27] flex items-center gap-1"><AlertTriangle size={10} /> {t("travel.flagged_n", { n: tr.flagged_count })}</span>}
                </div>
              </div>
              <div className="flex items-end justify-between mt-3">
                <div className="text-[11px] text-[#747474]">{t("travel.items_count", { n: tr.items?.length })}{tr.qbo_refs?.employee_bill_id && <span className="ml-2 text-[#a38d5b]">· QBO {tr.qbo_refs.payment_status === "paid" ? "✓" : t("travel.qbo_synced")}</span>}</div>
                <div className="font-semibold text-[#475569] text-[18px]">${tr.total_amount?.toFixed(2)}</div>
              </div>
            </button>
          ))}
        </div>
      )}

      {showNew && <NewTravelModal policy={policy} departments={departments} expenseTypes={expenseTypes} onClose={() => setShowNew(false)} onSaved={() => { setShowNew(false); load(); }} />}
      {detail && (
        <TravelDetailModal
          travel={detail}
          user={user}
          policy={policy}
          departments={departments}
          expenseTypes={expenseTypes}
          onClose={() => setDetail(null)}
          onUpdated={async () => {
            await load();
            const fresh = (await api.get(`/travel/${detail.id}`)).data;
            setDetail(fresh);
          }}
        />
      )}
    </div>
  );
}
